import { delay, fromEvent, map, Observable, take, throwError, timeout } from 'rxjs';

/**
 * How long a caller may wait for the network before giving up and failing as it
 * would have without any waiting. Past this point the user has long stopped
 * expecting that screen, and answering it is more likely to surprise them than
 * to help.
 */
export const MAX_OFFLINE_WAIT_MS = 2 * 60_000;

/**
 * Pause between the `online` event and acting on it. The event fires when the
 * interface comes up, which is earlier than the moment DNS resolves and a
 * proxy/VPN finishes handshaking — reacting to the event itself tends to burn
 * the attempt on a connection that is not routable yet.
 */
export const RECONNECT_SETTLE_MS = 750;

/**
 * How long to wait before reading {@link isOffline}, instead of reading it the
 * moment a request fails.
 *
 * The flag lags the failure. A request already in flight when the link drops
 * dies a few dozen milliseconds later — Chrome's network stack reports
 * `ERR_INTERNET_DISCONNECTED` while `navigator.onLine` is still `true`, and
 * only catches up right after. Deciding on the spot classifies those as "not a
 * connectivity problem" and drops exactly the failures worth acting on.
 */
export const OFFLINE_CONFIRM_MS = 1_000;

/**
 * Whether the device says it has no network.
 *
 * `navigator.onLine` is only trustworthy in this direction: `false` confirms
 * there is no network, while `true` proves nothing (a captive portal, a dropped
 * VPN and hotel Wi-Fi all report `true`). So this answers "is it certainly
 * offline?", never "is it really online?" — which is exactly the question worth
 * asking before deciding to wait for an `online` event that would otherwise
 * never come.
 */
export function isOffline(): boolean {
  return typeof navigator !== 'undefined' && navigator.onLine === false;
}

/** Thrown by {@link whenBackOnline} when the outage outlasts the wait. */
export class OfflineWaitTimeoutError extends Error {
  constructor() {
    super(`Still offline after ${MAX_OFFLINE_WAIT_MS}ms`);
    this.name = 'OfflineWaitTimeoutError';
  }
}

/**
 * Emits once — after the device is back online and the network has had a moment
 * to settle — and completes.
 *
 * It *errors* on timeout rather than completing quietly, because a caller that
 * drives an RxJS `retry` on this needs the failure path: a notifier that
 * completed without emitting would complete the retried request without ever
 * delivering a response or an error, leaving the subscriber hanging with no
 * sign anything went wrong. `timeoutReason` is what it throws, so an HTTP
 * caller can hand its own original `HttpErrorResponse` back to the app instead
 * of a generic timeout.
 *
 * The `online` listener is owned by the subscription: unsubscribing (a
 * destroyed component, a `switchMap` that moved on) removes it.
 */
export function whenBackOnline(timeoutReason?: unknown): Observable<void> {
  const failure = () =>
    throwError(() => timeoutReason ?? new OfflineWaitTimeoutError());

  if (typeof window === 'undefined') {
    return failure();
  }

  return fromEvent(window, 'online').pipe(
    take(1),
    timeout({ first: MAX_OFFLINE_WAIT_MS, with: failure }),
    delay(RECONNECT_SETTLE_MS),
    map(() => undefined)
  );
}
